import { rawInitData } from './telegram.js';

export interface SessionResponse {
  token: string;
  expiresAt: string;
  user: {
    id: string;
    telegramId: string;
    firstName: string | null;
    username: string | null;
    locale: 'fa' | 'en';
    timezone: string;
    globalRole: string;
  };
  connected: boolean;
  site: { tenantId: string; name: string; url: string; provider: string } | null;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly detail?: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
let session: SessionResponse | null = null;
let pending: Promise<SessionResponse> | null = null;

/**
 * The bearer token lives only in memory. A Mini App can always re-mint a
 * session from initData, so persisting a credential in localStorage would add
 * exposure without adding capability.
 */
export function currentSession(): SessionResponse | null {
  return session;
}

export async function ensureSession(force = false): Promise<SessionResponse> {
  if (session && !force) return session;
  // Even a forced refresh joins an in-flight mint. Two requests failing with
  // 401 together would otherwise each mint a session and race to overwrite the
  // token, and the replay guard now rejects the second one anyway.
  if (pending) return pending;

  pending = (async () => {
    const initData = rawInitData();
    if (!initData) {
      throw new ApiError('InvalidMiniAppSession', 'Open this app from Telegram.');
    }
    const response = await fetch('/auth/session', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ initData }),
    });
    const data = await parse<SessionResponse>(response);
    token = data.token;
    session = data;
    return data;
  })();

  try {
    return await pending;
  } finally {
    pending = null;
  }
}

async function parse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let body: unknown;
  try {
    body = text.length > 0 ? JSON.parse(text) : null;
  } catch {
    body = null;
  }

  if (!response.ok) {
    const error = body as { error?: string; message?: string; detail?: string } | null;
    throw new ApiError(
      error?.error ?? 'Internal',
      error?.message ?? `Request failed (${response.status})`,
      error?.detail,
      response.status,
    );
  }
  return body as T;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Retry once with a fresh session when the current one is rejected. */
  retryOnAuth?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!token) await ensureSession();

  const send = async (): Promise<Response> =>
    fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    });

  let response = await send();

  if (response.status === 401 && (options.retryOnAuth ?? true)) {
    await ensureSession(true);
    response = await send();
  }

  return parse<T>(response);
}

export async function upload<T>(path: string, file: File): Promise<T> {
  if (!token) await ensureSession();
  const form = new FormData();
  form.append('file', file, file.name);
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return parse<T>(response);
}

export function resetSession(): void {
  token = null;
  session = null;
}
